import { useMemo } from 'react'
import { useQuery } from 'react-query'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useCurrentUser } from '@modules-deprecated/app/user'

import { QueryKeys } from '../../../../enums/queryKeys'
import { getApiCountryKey } from '../../../../utils/getApiCountryKey'
import { getCurrentLocale } from '../../../../utils/getCurrentLocale'
import { fetchCategorySuggestionsNewMLModel, FetchCategorySuggestionsOptions } from '../query-api'
import { TrainingData } from '../types/trainingData'
import { useBookkeepingCategories } from './useBookkeepingCategories'

interface UseBookkeepingCategoriesMLSearchProps {
  searchQuery?: string
  trainingData?: TrainingData
  isEnabled?: boolean
}

export const useBookkeepingCategoriesMLSearch = ({
  searchQuery = '',
  trainingData,
  isEnabled = true,
}: UseBookkeepingCategoriesMLSearchProps) => {
  const { organization } = useUserOrganization()
  const { user } = useCurrentUser()
  const locale = getCurrentLocale()
  const isUserAndOrganizationLoaded = !!user && !!organization

  /* eslint-disable @typescript-eslint/naming-convention */
  const options: FetchCategorySuggestionsOptions | undefined = useMemo(
    () =>
      isUserAndOrganizationLoaded
        ? {
            text_input: searchQuery,
            session_id: trainingData?.id || '',
            contact_vat_number: trainingData?.contact?.vatNo || '',
            contact_reg_number: trainingData?.contact?.registrationNo || '',
            contact_country: trainingData?.contact?.countryId || '',
            organisation_country_code: getApiCountryKey(organization?.countryId).toUpperCase(),
            user_locale: user.locale,
            organization_id: organization.id,
            scanned_category_result: trainingData?.scannedCategoryResult || null,
            meta: {
              user_id: user.id,
              file_id: null,
              category_id: null,
            },
          }
        : undefined,

    [
      isUserAndOrganizationLoaded,
      organization?.countryId,
      organization?.id,
      searchQuery,
      trainingData?.contact?.countryId,
      trainingData?.contact?.registrationNo,
      trainingData?.contact?.vatNo,
      trainingData?.id,
      trainingData?.scannedCategoryResult,
      user?.id,
      user?.locale,
    ],
  )

  const { data: categorySuggestionsData, isLoading: isLoadingCategorySuggestionsData } = useQuery(
    [QueryKeys.BookkeepingCategorySuggestions, options],
    () =>
      fetchCategorySuggestionsNewMLModel({
        ...(options as FetchCategorySuggestionsOptions),
        isModelSearchEnabled: true,
      }),
    {
      enabled: isEnabled && isUserAndOrganizationLoaded && !!searchQuery,
      cacheTime: 0,
      keepPreviousData: false,
    },
  )

  const categoriesIds = useMemo(
    () => categorySuggestionsData?.suggested.map(({ id }) => id) || [],
    [categorySuggestionsData?.suggested],
  )

  const queryId = useMemo(() => categorySuggestionsData?.query_id, [categorySuggestionsData])

  const { suggestedCategories, isLoading: isLoadingCategoriesData } = useBookkeepingCategories({
    locale,
    isEnabled: !!categoriesIds.length,
    categoryIds: categoriesIds,
  })

  return {
    isLoading: isLoadingCategorySuggestionsData || isLoadingCategoriesData,
    queryId,
    suggestedCategories,
  }
}
